<template>
  <b-card>
    <div class="mb-5 flex justify-between">
      <div class="uppercase text-gray-300 text-lg">{{ $t('profile_experiences.title') }}</div>

      <div class="flex gap-2" v-if="isOwner && companies.length > 0">
        <!-- LinkedIn import button -->
        <b-button size="sm" light @click="linkedInImportIsOpen = true" data-e2e="profile-linkedin-import-button">
          <icon name="mdi:linkedin" class="h-4 w-4 mr-1" />
          {{ $t('profile_experiences.linkedin_import_button') }}
        </b-button>
        <b-modal-linked-in-import
          v-model="linkedInImportIsOpen"
          :company-id="currentCompanyId"
          :existing-experiences="experiences"
          @imported="fetchExperiences"
        />
        <b-button size="sm" @click="experienceCreationIsOpen = true" data-e2e="profile-add-experience-button">{{ $t('profile_experiences.add_experience_button') }}</b-button>
        <b-modal-create-experience v-model="experienceCreationIsOpen" :experience="experience" @experience-created="fetchExperiences" />
      </div>
    </div>

    <div v-if="isOwner && companies.length === 0">
      <b-alert
        :title="$t('experience.no_company_alert_title')"
        :description="$t('experience.no_company_alert_description')"
        variant="primary"
      >
        <template #actions="{ variant }">
          <b-button :variant="variant" size="sm" @click="$router.push('/company/create')">
            {{ $t('experience.add_company_button') }}
          </b-button>
        </template>
      </b-alert>
    </div>
    <div v-else class="flex flex-col gap-4">
      <template v-if="experiences.length > 0">
        <b-experience-card v-for="experience in experienceSorted" :experience="experience" />
        <BLogoAttribution />
      </template>
      <template v-else>
        <div class="text-center text-gray-400 p-8" v-if="isOwner">
          {{ $t('profile_experiences.no_experience_found_owner') }}
          <b-button class="mx-auto mt-2" size="sm" @click="experienceCreationIsOpen = true">{{ $t('profile_experiences.add_experience_button') }}</b-button>
        </div>
        <div v-else class="text-center text-gray-400 p-8">
          {{ $t('profile_experiences.no_experience_found_visitor') }}
        </div>
      </template>
    </div>

    <BModalDeleteExperience />



  </b-card>
</template>

<script setup lang="ts">
import BCard from "~/components/card/BCard.vue";
import BExperienceCard from "~/components/card/BExperienceCard.vue";
import BLogoAttribution from "~/components/base/BLogoAttribution.vue";
import BButton from "~/components/forms/BButton.vue";
import BModalCreateExperience from "~/components/modal/experience/BModalCreateExperience.vue";
import BModalDeleteExperience from "~/components/modal/experience/BModalDeleteExperience.vue";
import BModalLinkedInImport from "~/components/modal/experience/BModalLinkedInImport.vue";
import BTooltip from "~/components/BTooltip.vue";
import { authStore } from "~/stores/auth";
import { useCompanyStore } from "~/stores/companyStore";
import BAlert from "~/components/alert/BAlert.vue";
import type { User } from '~/stores/userStore';

const props = defineProps({
  user: {
    type: Object as PropType<User>,
    required: true,
  }
})

const experienceCreationIsOpen = inject<Ref<boolean>>('experienceCreationIsOpen', ref(false))
const linkedInImportIsOpen = ref(false)

const experienceStore = useExperienceStore()
const store = authStore()
const companyStore = useCompanyStore();
const route = useRoute()
const router = useRouter()

const { experiences, experience } = storeToRefs(experienceStore)
const { user: me } = storeToRefs(store);
const { companies } = storeToRefs(companyStore);

const { isPreview } = useProfilePreviewMode();
const isOwner = computed(() => !isPreview.value && me.value && props.user && me.value.id === props.user.id);

// Get current company ID from cookie or first company
const currentCompanyId = computed(() => {
  const currentCompanyCookie = useCookie('currentCompany');
  if (currentCompanyCookie.value) return currentCompanyCookie.value;
  if (companies.value.length > 0) return companies.value[0].company_id;
  return '';
});

const fetchExperiences = () => {
  if (props.user) {
    experienceStore.fetchExperiences({ user_id: props.user.id, includes: ['feedbacks'] })
  }
}

watch(() => props.user, (value) => {
  if (value) {
    fetchExperiences()
  }
}, { immediate: true });


onMounted(() => {
  if (me.value) {
    companyStore.fetchCompanies();
  }
})

watch(() => route.query, (value) => {
  if (value.action === 'update' && value.experience) {
    experienceStore.fetchExperience(value.experience as string, { includes: ['feedbacks'] })
    experienceCreationIsOpen.value = true
  }
}, { immediate: true });

watch(() => experienceCreationIsOpen.value, (value) => {
  if (!value) {

    const query = { ...route.query }

    delete query.action
    delete query.experience

    router.push({ ...route, query })
  }
}, { immediate: true })

const experienceSorted = computed(() => {
  return [...experiences.value].sort((a, b) => {
    const aIsCurrent = !a.end_at || a.end_at === null
    const bIsCurrent = !b.end_at || b.end_at === null

    if (aIsCurrent && !bIsCurrent) return -1
    if (!aIsCurrent && bIsCurrent) return 1

    // Both current: most recently started first
    if (aIsCurrent && bIsCurrent) {
      return new Date(b.start_at).getTime() - new Date(a.start_at).getTime()
    }

    // Both ended: most recently ended first, then by start_at as tiebreaker
    const endDiff = new Date(b.end_at).getTime() - new Date(a.end_at).getTime()
    if (endDiff !== 0) return endDiff
    return new Date(b.start_at).getTime() - new Date(a.start_at).getTime()
  })
})
</script>
